import { TriggerStepCreate, When } from "@dots-projects/types";
import { useState } from "react";
import { addSteps, saveDraft } from "./reducer/actionCreators";
import { useTriggerEditorStateDispatch } from "./TriggerEditor";
import { Input } from "antd";
import {
  STEP_TEMPLATES,
  StepTemplate,
  createStepsFromTemplate,
} from "./stepTemplates";
import { SearchOutlined } from "@ant-design/icons";
import clsx from "clsx";
import { WarningOutlined } from "@ant-design/icons";
import { PlaceholderData } from "./reducer/stateUtils";
import invariant from "ts-invariant";
import { canAddEphemeralStep } from "./reducer/triggerUtils";

export type AddItemData = {
  parentStepId: string;
  when?: When;
};

function filterAddCandidates(stepTemplates: StepTemplate[], _parentId: string) {
  // TODO Apply the mapping here
  return stepTemplates.filter((t) => !t.internal);
}

/**
 *
 * The component displayed when the placeholder node ("+") in the editor is selected. User can then click an item to insert into the editor.
 *
 */
export function AddItemForm({ data }: { data: PlaceholderData }) {
  const { state, dispatch, onPersist } = useTriggerEditorStateDispatch();
  const [actionSearch, setActionSearch] = useState("");
  const filteredTemplates = filterAddCandidates(
    STEP_TEMPLATES,
    data.parentStepId
  );

  const steps = state.trigger.current.steps;

  //parent id always will be idle because we are adding a new step and refers to the (+) node except when the first step is added
  let parentStep: TriggerStepCreate | undefined = steps.find(
    (step) => step.id === data.parentStepId
  );
  let canAddShowModalTask = false;

  if (parentStep) {
    if (parentStep?.type === "idle") {
      // Check the parent of the idle step (+) node to check if it's a task that can show a modal
      parentStep = steps.find((step) =>
        step.transitions
          .map((t) => t.targetStepId)
          .includes(parentStep?.id as string)
      ) as TriggerStepCreate;
    }
    //TODO: add support for send_multiple_select
    canAddShowModalTask = ["send_survey"].includes(parentStep?.type as string);
  } else {
    invariant(state.trigger.current.isDraft);
    canAddShowModalTask = ["button_click", "command"].includes(
      state.trigger.current.liveTriggerConfigs.type
    );
  }

  const isStepDisabled = (stepTemplate: StepTemplate) => {
    const hasCreateChannelStep = !!state.trigger.current.steps.filter(
      (step) => step.type === "create_channel"
    ).length;

    const disableModal =
      stepTemplate.steps().some((step) => step.type === "open_modal") &&
      !canAddShowModalTask;

    // ephemeral captcha can't be selected if there is no parent step that generates an interaction
    const disableEphemeralCaptcha =
      stepTemplate.title === "Send Captcha (visible only to the user)" &&
      !canAddEphemeralStep(
        state.trigger.current,
        parentStep ? parentStep.type : ""
      );

    return (
      (!hasCreateChannelStep &&
        stepTemplate
          .steps()
          .some(
            (stp) =>
              typeof stp === "object" &&
              (stepTemplate.title === "Send Captcha" ||
                stp.type === "delete_channel")
          )) ||
      disableModal ||
      disableEphemeralCaptcha ||
      stepTemplate.title === "API Step (Make a HTTP request)"
    );
  };

  return (
    <form className="flex flex-col gap-4">
      <Input
        prefix={<SearchOutlined />}
        id="action-search"
        name="action-search"
        placeholder="Search for an action type"
        value={actionSearch}
        onChange={(event) => setActionSearch(event.target.value)}
      />
      {filteredTemplates
        .filter(
          (f) =>
            f.title.toLowerCase().includes(actionSearch.toLowerCase()) ||
            f.description.toLowerCase().includes(actionSearch.toLowerCase()) ||
            actionSearch === ""
        )
        .map((stepTemplate) => (
          <button
            className={clsx(
              `text-left hover:bg-dots-light-orange hover:bg-opacity-25 rounded-md border border-gray-300 p-3`,
              {
                // this style disables the delete channel step when an add channel step has not been previously created
                "pointer-events-none	opacity-50": isStepDisabled(stepTemplate),
              }
            )}
            key={stepTemplate.title}
            onClick={(e) => {
              e.preventDefault();
              const finalSteps = createStepsFromTemplate(
                stepTemplate,
                state.trigger.current,
                data.parentStepId
              );
              dispatch(
                addSteps(finalSteps, {
                  parentStepId: data.parentStepId,
                  isParentTriggerRoot: data.isParentTriggerRoot,
                  when: data.when,
                  transitionIndex: data.transitionIndex,
                })
              );
              dispatch(saveDraft(onPersist));
              window.analytics.track(
                `Trigger - new step added ${stepTemplate.title}`
              );
            }}
          >
            <div className="font-semibold">{stepTemplate.title}</div>
            <div>{stepTemplate.description}</div>
            {(() => {
              if (
                isStepDisabled(stepTemplate) &&
                (stepTemplate
                  .steps()
                  .some((step) => step.type === "open_modal") ||
                  stepTemplate.title ===
                    "Send Captcha (visible only to the user)")
              ) {
                return (
                  <div>
                    <WarningOutlined /> Requires a parent step of type "Send
                    Survey" or a trigger of type "Button Click" or "Command"
                  </div>
                );
              } else if (
                stepTemplate.title === "API Step (Make a HTTP request)"
              ) {
                return (
                  <div>
                    <WarningOutlined /> Your plan isn’t eligible for this.
                    Please contact support
                  </div>
                );
              } else if (isStepDisabled(stepTemplate)) {
                return (
                  <div>
                    <WarningOutlined /> Requires a create channel step
                  </div>
                );
              }
              return "";
            })()}
          </button>
        ))}
      <button className="text-left hover:bg-dots-light-orange hover:bg-opacity-25 rounded-md border border-gray-300 p-3">
        <div>
          Don’t see one you want? Request one{" "}
          <a href="mailto:hello@dots.community">here!</a>
        </div>
      </button>
    </form>
  );
}
